import { useCallback } from 'react'
import { shapes } from '../../lib/joint'
import { ringLayout } from '../../lib/ringLayout'
import { JointCanvas } from '../../components/JointCanvas'
import type { ContactView } from '../../types/contacts'

// The influence map: the firm at the centre, the accounts in a ring around it, each account's people
// around it. A node's colour is the person's ROLE (customer, prospect, vendor, partner, colleague,
// competitor), its size their relationship strength, its badge their PERSONA on the pursuit (champion,
// influencer, challenger, decision-maker, gatekeeper). Click a person to open them; click an account to go
// to its plan (only when the group is a real company — a name-only group has no page to open). Built on
// @joint/core through the shared JointCanvas (the account plan's map is the same canvas with a different layout).

const ROLE_FILL: Record<string, string> = {
  customer: '#0f9e8e', prospect: '#2f80ed', vendor: '#8e6cc2', partner: '#1e8449', colleague: '#9aa5b5', competitor: '#c0392b', other: '#b0b8c4',
}
const PERSONA_GLYPH: Record<string, string> = { champion: '★', influencer: '◆', challenger: '▲', 'decision-maker': '■', gatekeeper: '●', user: '○' }
/** The rep's vote on the tooltip (REQ-123); the order the map draws in already comes from rankContacts, vote first. */
const voteWords = (vote: number | null) => ((vote ?? 0) > 0 ? ' · important' : (vote ?? 0) < 0 ? ' · less' : '')

export function InfluenceMap({ contacts, firmName, onOpen, onOpenAccount }: {
  contacts: ContactView[]; firmName: string; onOpen: (personId: string) => void; onOpenAccount?: (companyId: string) => void
}) {
  const build = useCallback((model: any, w: number, h: number) => {
    const cx = w / 2; const cy = h / 2

    // the firm
    const firm = new shapes.standard.Circle({ position: { x: cx - 36, y: cy - 36 }, size: { width: 72, height: 72 },
      attrs: { body: { fill: '#0b7c70', stroke: '#0b7c70' }, label: { text: firmName, fill: '#fff', fontSize: 11, fontWeight: 700, textWrap: { width: 64 } } } })
    firm.addTo(model)

    // accounts in a ring, people around each
    const byCompany = new Map<string, ContactView[]>()
    for (const c of contacts.filter((c) => c.person.role !== 'colleague')) {
      const k = c.person.companyId ?? c.companyName
      if (!byCompany.has(k)) byCompany.set(k, [])
      byCompany.get(k)!.push(c)
    }
    const colleagues = contacts.filter((c) => c.person.role === 'colleague')
    const accounts = [...byCompany.entries()].sort((a, b) => b[1].length - a[1].length).slice(0, 40)
    // rings sized to the book (REQ-109, "I can't press on the dots"): a card and its people need an arc of their own,
    // so forty accounts take three rings rather than one crowded one; the canvas fits the drawing and the wheel zooms
    const slots = ringLayout(accounts.length, cx, cy, Math.min(w, h) * 0.34)
    // the cards first, every one of them, and the people after — a dot is never under a neighbouring card
    const cards = accounts.map(([key, people], i) => {
      const { x: ax, y: ay, angle: a } = slots[i]
      const acct = new shapes.standard.Rectangle({ position: { x: ax - 56, y: ay - 16 }, size: { width: 112, height: 32 },
        attrs: { body: { fill: '#ffffff', stroke: '#dfe5ee', rx: 6, ry: 6 }, label: { text: people[0].companyName || key, fontSize: 11, fill: '#33415c', textWrap: { width: 104, ellipsis: true } } } })
      // the group key is the companyId when the person has one, else the bare name — only the former opens
      if (people[0].person.companyId) (acct as any).accountId = people[0].person.companyId
      acct.addTo(model)
      new shapes.standard.Link({ source: { id: firm.id }, target: { id: acct.id }, attrs: { line: { stroke: '#dfe5ee', strokeWidth: 1.2, targetMarker: { type: 'none' } } } }).addTo(model)
      return { acct, ax, ay, a, people }
    })
    cards.forEach(({ acct, ax, ay, a, people }) => {
      const shown = people.slice(0, 8)
      shown.forEach((c, j) => {
        const spread = Math.min(Math.PI * 0.9, 0.35 * shown.length)
        const pa = a + (shown.length === 1 ? 0 : -spread / 2 + (j / (shown.length - 1)) * spread)
        const pr = 74 + (j % 2) * 22
        const px = ax + Math.cos(pa) * pr; const py = ay + Math.sin(pa) * pr
        const strength = c.person.relationshipStrength ?? 0
        const d = 14 + Math.round(strength * 22)
        const role = c.person.role ?? 'other'
        const glyph = c.person.persona ? PERSONA_GLYPH[c.person.persona] ?? '' : ''
        const node = new shapes.standard.Circle({ position: { x: px - d / 2, y: py - d / 2 }, size: { width: d, height: d },
          attrs: { body: { fill: ROLE_FILL[role] ?? ROLE_FILL.other, stroke: '#ffffff', strokeWidth: 1.5 },
            label: { text: glyph, fill: '#fff', fontSize: Math.max(8, d - 8), fontWeight: 700 } } })
        ;(node as any).personId = c.person.personId
        ;(node as any).title = `${c.person.name}${c.person.title ? ' · ' + c.person.title : ''} · ${role}${c.person.persona ? ' · ' + c.person.persona : ''} · ${Math.round(strength * 100)}%${voteWords(c.vote)}`
        node.addTo(model)
        new shapes.standard.Link({ source: { id: acct.id }, target: { id: node.id }, attrs: { line: { stroke: '#e6ebf2', strokeWidth: 1, targetMarker: { type: 'none' } } } }).addTo(model)
      })
    })
    // colleagues cluster under the firm
    colleagues.slice(0, 12).forEach((c, j) => {
      const a = Math.PI / 2 + (j - (Math.min(colleagues.length, 12) - 1) / 2) * 0.22
      const px = cx + Math.cos(a) * 84; const py = cy + Math.sin(a) * 84
      const node = new shapes.standard.Circle({ position: { x: px - 8, y: py - 8 }, size: { width: 16, height: 16 }, attrs: { body: { fill: ROLE_FILL.colleague, stroke: '#fff' } } })
      ;(node as any).personId = c.person.personId; (node as any).title = `${c.person.name} · colleague${voteWords(c.vote)}`
      node.addTo(model)
      new shapes.standard.Link({ source: { id: firm.id }, target: { id: node.id }, attrs: { line: { stroke: '#e6ebf2', strokeWidth: 1, targetMarker: { type: 'none' } } } }).addTo(model)
    })
  }, [contacts, firmName])

  return (
    <JointCanvas
      build={build}
      deps={[build]}
      onOpen={onOpen}
      onOpenAccount={onOpenAccount}
      legend={
        <>
          {Object.entries(ROLE_FILL).filter(([k]) => k !== 'other').map(([k, v]) => <span key={k}><i style={{ background: v }} />{k}</span>)}
          <span className="muted small">size = strength · {Object.entries(PERSONA_GLYPH).map(([k, g]) => `${g} ${k}`).join(' · ')}</span>
        </>
      }
    />
  )
}
